import { useRef, useState, useEffect } from 'react';
import { Button, message, Spin, Popconfirm, Drawer, Tag, Tooltip, Divider } from 'antd';
import Card from 'components/Card/Card';
import Form from 'components/Form';
import { Banner } from 'components/ui/Banner';
import { PROVIDER_APP_PAYMENT_METHODS } from 'containers/Provider';
import { default as providerService } from 'services/providers.service';
import BankingProfileForm from 'components/BankingProfileForm';
import BankingProfileSummary from 'components/BankingProfileSummary';
import { Add, Information, Locked } from '@carbon/icons-react';
import { useFlags } from 'launchdarkly-react-client-sdk';

export default function BankingProfiles({
  organization,
  onProfilesChange,
  isEditable = true,
  collapsible,
  open,
  onOpenChange,
  extra,
}) {
  const { providerBankingProfileEditing } = useFlags();
  const [form] = Form.useForm();
  const [isAdding, setIsAdding] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Holds the staged voided check File for the current drawer session
  const stagedVoidedCheckRef = useRef(null);

  useEffect(() => {
    if (organization?.id) {
      fetchProfiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id]);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const data = await providerService.getBankingProfiles(organization.id);
      setProfiles(data || []);
      onProfilesChange?.(data || []);
    } catch (error) {
      console.error('Error fetching banking profiles:', error);
      message.error('Failed to load banking profiles');
    } finally {
      setLoading(false);
    }
  };

  const openAddDrawer = () => {
    stagedVoidedCheckRef.current = null;
    setEditingProfile(null);
    form.resetFields();
    setIsAdding(true);
  };

  const openEditDrawer = (profile) => {
    stagedVoidedCheckRef.current = null;
    setEditingProfile(profile);

    form.setFieldsValue({
      profileName: profile.profileName,
      paymentMethod: profile.directDeposit
        ? PROVIDER_APP_PAYMENT_METHODS.DIRECT_DEPOSIT
        : PROVIDER_APP_PAYMENT_METHODS.PAPER_CHECK,
      paymentName: profile.paymentName,
      banking:
        profile.accountNumber || profile.routingNumber
          ? {
              accountType: profile.accountType,
              bankName: profile.bankName,
              routingNumber: profile.routingNumber,
              accountNumber: profile.accountNumber,
            }
          : undefined,
    });

    setIsAdding(true);
  };

  const closeDrawer = () => {
    stagedVoidedCheckRef.current = null;
    setIsAdding(false);
    setEditingProfile(null);
    form.resetFields();
  };

  const handleSaveProfile = async (values) => {
    try {
      setSubmitting(true);

      const bankingDto = {
        profileName: values.profileName,
        paymentName: values.paymentName,
        directDeposit: values.paymentMethod === PROVIDER_APP_PAYMENT_METHODS.DIRECT_DEPOSIT,
        accountType: values.banking?.accountType || null,
        bankName: values.banking?.bankName || null,
        routingNumber: values.banking?.routingNumber || null,
        accountNumber: values.banking?.accountNumber || null,
      };

      let savedProfile;
      if (editingProfile) {
        savedProfile = await providerService.updateBankingProfile(organization.id, editingProfile.id, bankingDto);
        message.success('Banking profile updated successfully');
      } else {
        savedProfile = await providerService.createBankingProfile(organization.id, bankingDto);
        message.success('Banking profile created successfully');
      }

      // Upload staged voided check against the now-known profile ID
      if (stagedVoidedCheckRef.current) {
        try {
          await providerService.uploadVoidedCheck(organization.id, savedProfile.id, stagedVoidedCheckRef.current);
        } catch (err) {
          message.warning('Profile saved but voided check upload failed. Please try uploading it again.');
          console.error('Voided check upload failed:', err);
        }
      }

      closeDrawer();
      await fetchProfiles();
    } catch (error) {
      console.error('Error saving banking profile:', error);
      message.error(error.response?.data?.message || 'Failed to save banking profile');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProfile = async (profileId) => {
    try {
      await providerService.deleteBankingProfile(organization.id, profileId);
      message.success('Banking profile deleted successfully');
      await fetchProfiles();
    } catch (error) {
      console.error('Error deleting banking profile:', error);
      if (error.response?.status === 409) {
        message.error('Cannot delete a profile that is currently active on the organization or any facility');
      } else {
        message.error('Failed to delete banking profile');
      }
    }
  };

  if (loading) {
    return (
      <Card
        title={
          <Banner
            variant="card-title"
            question="Banking profiles"
            guidance="Save your organization's payment details. Add more than one if you need to split payments across separate accounts"
            className="my-8"
          />
        }
        extra={extra}
        className="mb-8"
        data-testid="bankingProfilesCard"
      >
        <div className="flex justify-center py-4">
          <Spin />
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        <Banner
          variant="card-title"
          question="Banking profiles"
          guidance="Save your organization's payment details. Add more than one if you need to split payments across separate accounts"
          className="my-8"
        />
      }
      extra={extra}
      className="mb-8"
      data-testid="bankingProfilesCard"
      collapsible={collapsible}
      open={open}
      onOpenChange={onOpenChange}
    >
      {profiles.length === 0 && isEditable && (
        <Button type="link" onClick={openAddDrawer} className="p-0 text-blue-500 hover:text-blue-600">
          <Add className="ml-[-5px]" size={18} /> Add a banking profile
        </Button>
      )}

      {profiles.length > 0 && (
        <>
          {profiles.map((profile) => {
            const facilityNames = profile.careFacilities?.map((f) => f.name).join(', ');
            const isProfileLocked = profile.careFacilities?.length > 0;

            return (
              <div key={profile.id} className="border border-solid border-gray-200 rounded-lg p-4 mb-4">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center">
                  <div>
                    <div className="font-semibold text-base flex items-center gap-2">
                      {profile.profileName}

                      {profile.activeOnOrganization && <Tag color="green">active</Tag>}

                      {!isProfileLocked && !!profile.careFacilities?.length && (
                        <Tooltip title={facilityNames}>
                          <Tag
                            color="blue"
                            className="flex items-center gap-1 cursor-default"
                            style={{ width: 'fit-content' }}
                          >
                            <Information size={14} />
                            used by {profile.careFacilities.length} location
                            {profile.careFacilities.length !== 1 ? 's' : ''}
                          </Tag>
                        </Tooltip>
                      )}
                    </div>

                    <BankingProfileSummary profile={profile} className="mt-2" />
                  </div>

                  {!isProfileLocked && isEditable && providerBankingProfileEditing && (
                    <div className="flex gap-2 justify-end mt-3 pt-3 border-t border-solid border-gray-200 md:mt-0 md:pt-0 md:border-0 md:justify-start">
                      {!profile.active && (
                        <>
                          <Button type="link" className="text-blue-500" onClick={() => openEditDrawer(profile)}>
                            Edit
                          </Button>

                          <Popconfirm
                            title="Delete banking profile"
                            description="Are you sure you want to delete this profile?"
                            onConfirm={() => handleDeleteProfile(profile.id)}
                            okText="Delete"
                            okButtonProps={{ danger: true }}
                            cancelText="Cancel"
                          >
                            <Button type="link" danger>
                              Delete
                            </Button>
                          </Popconfirm>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {isProfileLocked && (
                  <>
                    <Divider className="mt-8 mb-4" />

                    <div
                      className="flex items-start gap-2 mt-3 text-sm text-gray-600"
                      data-testid="banking-profile-locked-message"
                    >
                      <Locked size={16} className="ml-[-2px] shrink-0" />

                      <span>
                        Locked &mdash; referenced by submitted location. Contact your coordinator to make changes.
                      </span>
                    </div>
                  </>
                )}

                {!isProfileLocked && profile.active && (
                  <>
                    <Divider className="mt-8 mb-4" />

                    <div
                      className="flex items-start gap-2 mt-3 text-sm text-gray-600"
                      data-testid="banking-profile-active-message"
                    >
                      <Locked size={16} className="ml-[-2px] shrink-0" />

                      <span>
                        This is your organization&rsquo;s active banking profile and cannot be deleted while in use.
                      </span>
                    </div>
                  </>
                )}
              </div>
            );
          })}

          {isEditable && (
            <Button type="link" onClick={openAddDrawer} className="p-0 text-blue-500 hover:text-blue-600">
              <Add className="ml-[-5px]" size={18} /> Add another banking profile
            </Button>
          )}
        </>
      )}

      <Drawer
        title={editingProfile ? 'Update Banking Profile' : 'Add Banking Profile'}
        placement="right"
        onClose={closeDrawer}
        open={isAdding}
        width={640}
        footer={
          <div className="flex justify-end gap-3">
            <Button size="large" onClick={closeDrawer} disabled={submitting}>
              Cancel
            </Button>
            <Button type="primary" size="large" onClick={() => form.submit()} loading={submitting}>
              {editingProfile ? 'Update profile' : 'Save profile'}
            </Button>
          </div>
        }
      >
        <Form form={form} onFinish={handleSaveProfile} layout="vertical">
          <BankingProfileForm
            form={form}
            orgId={organization?.id}
            careFacilities={editingProfile?.careFacilities}
            voidedCheckFileId={editingProfile?.voidedCheckFileId}
            voidedCheckFileName={editingProfile?.voidedCheckFileName}
            onVoidedCheckStaged={(file) => {
              stagedVoidedCheckRef.current = file;
            }}
          />
        </Form>
      </Drawer>
    </Card>
  );
}
